import type {
  AdminCrewReportImportItem,
  AdminCrewReportImportReport,
  AdminCrewReportImportRunExecutor,
  AdminCrewReportImportRunResult,
} from "../models/admin-crew-report.models";
import {
  ADMIN_CREW_REPORT_IMPORT_CONTINUATION_MESSAGE,
  ADMIN_CREW_REPORT_IMPORT_PDF_DOCUMENT_TITLE_PREFIX,
  ADMIN_CREW_REPORT_IMPORT_PDF_TIMESTAMP_MILLISECONDS_PATTERN,
  ADMIN_CREW_REPORT_IMPORT_PDF_TIMESTAMP_SEPARATORS_PATTERN,
  ADMIN_CREW_REPORT_IMPORT_PRINT_BODY_CLASS,
  ADMIN_CREW_REPORT_IMPORT_PRINT_PAGE_STYLE,
  ADMIN_CREW_REPORT_IMPORT_PRINT_PAGE_STYLE_ID,
} from "./admin-crew-report.constants";

export function getAdminCrewReportImportEmailGroups(items: AdminCrewReportImportItem[]): AdminCrewReportImportItem[][] {
  const groups = new Map<string, AdminCrewReportImportItem[]>();
  for (const item of items) groups.set(item.messageId, [...(groups.get(item.messageId) ?? []), item]);
  return Array.from(groups.values());
}

export function getAdminCrewReportImportSummaryMessage(report: AdminCrewReportImportReport): string {
  const outcome = report.outcome.replace(/_/g, " ").toLowerCase();
  const summary =
    `Import ${outcome}: ${report.counts.imported} imported, ${report.counts.failed} failed, ` +
    `${report.counts.superseded} superseded, and ${report.counts.alreadyProcessed} already processed.`;
  return report.continuationPending || report.retryableItemsPending
    ? `${summary} ${ADMIN_CREW_REPORT_IMPORT_CONTINUATION_MESSAGE}`
    : summary;
}

export async function executeAdminCrewReportImportRun(
  allowTokenlessInitialImports: boolean,
  executeRun: AdminCrewReportImportRunExecutor,
  initialRunCutoffAt?: string,
): Promise<AdminCrewReportImportRunResult> {
  const result = await executeRun({
    allowTokenlessInitialImports,
    ...(initialRunCutoffAt ? { runCutoffAt: initialRunCutoffAt } : {}),
  });

  if (initialRunCutoffAt && result.runCutoffAt !== initialRunCutoffAt) {
    throw new Error("The Crew Report import run returned a different run cutoff.");
  }

  return result;
}

export function getAdminCrewReportImportRunSummaryMessage(result: AdminCrewReportImportRunResult): string {
  if (result.interruption) {
    const batchLabel = result.batchCount === 1 ? "batch" : "batches";
    return (
      `Import stopped after ${result.batchCount} completed ${batchLabel}: ${result.counts.imported} imported, ` +
      `${result.counts.failed} failed, ${result.counts.superseded} superseded, and ` +
      `${result.counts.alreadyProcessed} already processed. Batch ${result.interruption.failedBatchNumber} failed: ` +
      result.interruption.reason
    );
  }

  const action =
    result.outcome === "FAILED"
      ? "stopped"
      : result.outcome === "COMPLETED_WITH_FAILURES"
        ? "completed with failures"
        : "completed";
  const batchLabel = result.batchCount === 1 ? "batch" : "batches";
  return (
    `Import ${action} after ${result.batchCount} ${batchLabel}: ${result.counts.imported} imported, ` +
    `${result.counts.failed} failed, ${result.counts.superseded} superseded, and ` +
    `${result.counts.alreadyProcessed} already processed.`
  );
}

export function printAdminCrewReportImportRunReport(result: AdminCrewReportImportRunResult): void {
  const finalReport = result.reports[result.reports.length - 1];
  if (!finalReport) {
    throw new Error("The Crew Report import run has no batch reports to export.");
  }

  const printDocumentTitle = buildAdminCrewReportImportPdfDocumentTitle(finalReport);
  const printPageStyle = createAdminCrewReportImportPrintPageStyle();
  const originalDocumentTitle = document.title;
  let cleanupTimeoutId: number | undefined;
  let cleanupCompleted = false;

  const cleanupPrintMode = () => {
    if (cleanupCompleted) {
      return;
    }

    cleanupCompleted = true;
    window.removeEventListener("afterprint", cleanupPrintMode);
    if (cleanupTimeoutId !== undefined) {
      window.clearTimeout(cleanupTimeoutId);
    }
    document.body.classList.remove(ADMIN_CREW_REPORT_IMPORT_PRINT_BODY_CLASS);
    document.title = originalDocumentTitle;
    printPageStyle.remove();
  };

  document.body.classList.add(ADMIN_CREW_REPORT_IMPORT_PRINT_BODY_CLASS);
  if (printDocumentTitle) {
    document.title = printDocumentTitle;
  }
  window.addEventListener("afterprint", cleanupPrintMode, { once: true });

  try {
    window.print();
  } catch (error) {
    cleanupPrintMode();
    throw error;
  }

  if (!cleanupCompleted) {
    cleanupTimeoutId = window.setTimeout(cleanupPrintMode, 60000);
  }
}

function createAdminCrewReportImportPrintPageStyle(): HTMLStyleElement {
  document.getElementById(ADMIN_CREW_REPORT_IMPORT_PRINT_PAGE_STYLE_ID)?.remove();

  const styleElement = document.createElement("style");
  styleElement.id = ADMIN_CREW_REPORT_IMPORT_PRINT_PAGE_STYLE_ID;
  styleElement.media = "print";
  styleElement.textContent = ADMIN_CREW_REPORT_IMPORT_PRINT_PAGE_STYLE;
  document.head.appendChild(styleElement);

  return styleElement;
}

function buildAdminCrewReportImportPdfDocumentTitle(report: AdminCrewReportImportReport): string {
  const timestamp = new Date(report.completedAt)
    .toISOString()
    .replace(ADMIN_CREW_REPORT_IMPORT_PDF_TIMESTAMP_SEPARATORS_PATTERN, "")
    .replace(ADMIN_CREW_REPORT_IMPORT_PDF_TIMESTAMP_MILLISECONDS_PATTERN, "Z");
  return `${ADMIN_CREW_REPORT_IMPORT_PDF_DOCUMENT_TITLE_PREFIX}-${report.environment}-${timestamp}`;
}
